import React, { useCallback, useState } from 'react';
import { Button, ControlLink, Field, LoadingBalls, OtpInput, Overlay, Stack, Text } from '@moda/om';
import { MINUTES_TO_MILLISECONDS, noop, useForm, useTimer } from '@moda/portal-stanchions';
import { usePasswordlessAuth } from '../../hooks/usePasswordlessAuth';
import { AuthPayloadSuccess } from '../../lib/authenticate';
import { RegistrationChallenge } from '../../lib/passwordless';

const PASSWORDLESS_CODE_LENGTH = 6;
const RESEND_DELAY_MS = MINUTES_TO_MILLISECONDS;

type PasswordlessOtpFormValues = {
  signInOtp: string;
};

const OTP_VALIDATIONS = {
  signInOtp: {
    required: {
      message: 'Please enter a valid 6 digit code.',
      value: true
    },
    pattern: {
      message: 'Please enter a valid 6 digit code.',
      value: /^\d{6}$/
    }
  }
};

type PasswordlessOtpStepProps = {
  challengeId: string;
  email: string;
  cancelLabel?: string;
  footer?: React.ReactNode;
  onAuth: (authUser?: AuthPayloadSuccess) => void;
  remember?: boolean;
  onCancel: () => void;
  onRegistrationRequired: (challenge: RegistrationChallenge) => void;
  title?: string;
};

export const PasswordlessOtpStep: React.FC<PasswordlessOtpStepProps> = ({
  challengeId,
  cancelLabel = 'Cancel',
  email,
  footer,
  onAuth,
  remember = true,
  onCancel,
  onRegistrationRequired,
  title = 'Sign In or Create an Account'
}) => {
  const { clearError, error, loading, resendEmail, verifyOtp } = usePasswordlessAuth();
  const [otp, setOtp] = useState('');
  const [hasResent, setHasResent] = useState(false);
  const [timeLeft] = useTimer(Date.now() + RESEND_DELAY_MS, true);

  const canResend = !hasResent && timeLeft.isOver;

  const handleVerifySubmit = useCallback(
    (values: PasswordlessOtpFormValues) => {
      const trimmedOtp = values.signInOtp.trim();

      verifyOtp({ challengeId, otp: trimmedOtp, remember })
        .then(result => {
          if (result.type === 'registration_required') {
            onRegistrationRequired(result.registration);
            return result;
          }

          onAuth(result.user);

          return result;
        })
        .catch(noop);
    },
    [challengeId, onAuth, onRegistrationRequired, remember, verifyOtp]
  );

  const handleOtpChange = useCallback(
    (value: string | number) => {
      if (error) clearError();
      const nextOtp = String(value).replace(/\D/g, '').slice(0, PASSWORDLESS_CODE_LENGTH);

      setOtp(nextOtp);

      if (!loading && nextOtp.length === PASSWORDLESS_CODE_LENGTH) {
        handleVerifySubmit({ signInOtp: nextOtp });
      }
    },
    [clearError, error, handleVerifySubmit, loading]
  );

  const handleResend = useCallback(() => {
    if (!canResend) return;

    resendEmail({ challengeId })
      .then(() => {
        setHasResent(true);
      })
      .catch(noop);
  }, [canResend, challengeId, resendEmail]);

  const { errors: fieldErrors, handleSubmit } = useForm({
    onSubmit: handleVerifySubmit,
    validations: OTP_VALIDATIONS
  });

  return (
    <>
      <form onSubmit={handleSubmit} data-testid="signInSection">
        {title && <div className="Authentication__title">{title}</div>}

        <div className="Authentication__message">{`We sent you a 6 digit code to your email ${email}.`}</div>

        <Stack space={5}>
          <div className="Authentication__fields">
            <Field
              className="Authentication__otp-input"
              id="signInOtp"
              name="signInOtp"
              label="Enter Code"
              error={fieldErrors.signInOtp?.message}
              showErrorIcon={false}
            >
              <OtpInput
                numInputs={PASSWORDLESS_CODE_LENGTH}
                onChange={handleOtpChange}
                value={otp}
              />
            </Field>
          </div>

          {error?.message && (
            <div className="Authentication__otp-error" role="alert">
              {error.message}
            </div>
          )}

          <Stack space={2}>
            <Button
              className="Authentication__button Authentication__button--sign-in"
              type="submit"
              disabled={loading || otp.length !== PASSWORDLESS_CODE_LENGTH}
              data-testid="signInSubmit"
            >
              CONTINUE
            </Button>

            <Button
              className="Authentication__button Authentication__button--cancel-sign-in"
              onClick={onCancel}
              secondary
              type="button"
            >
              {cancelLabel}
            </Button>
          </Stack>
        </Stack>
      </form>

      <div className="Authentication__divider" />
      <Stack space={2} alignItems="center">
        {hasResent ? (
          <Text>The email was resent.</Text>
        ) : (
          <Text>
            Didn't receive it?{' '}
            {canResend ? (
              <ControlLink onClick={handleResend} type="button">
                Resend
              </ControlLink>
            ) : (
              <Text treatment="bold1">
                Resend in {timeLeft.minutesLeft}:{timeLeft.secondsLeft}
              </Text>
            )}
          </Text>
        )}
        <Text>Code expires in 5 minutes</Text>
        {footer}
      </Stack>

      <Overlay className="Authentication__overlay" show={loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
